import { DerbyClock, type ClockFrame } from '../fixed-step';
import { DerbyRngV1 } from '../rng';
import { createAi, type AiController } from './ai';
import type {
  HorseParams,
  LaneState,
  RaceCommand,
  RaceEvent,
  RaceResult,
  RaceSetup,
  RaceState,
  StartQuality,
} from './types';

const MAX_STEPS_PER_FRAME = 10;
const EVENT_CAP = 256;

/**
 * The 30 Hz race sub-sim. Zero pixi/DOM imports; a race is a pure function
 * of (setup, player tap tape) — live play steps this same sim with tap
 * commands and records the tape as it applies them, so the balance
 * harness, the bounding tests, and later photo-finish replays all share
 * one code path.
 */
export class RaceSim {
  readonly setup: RaceSetup;
  readonly state: RaceState;
  /** Render-side slow-motion hook; never touched by the sim itself. */
  speed = 1;

  private readonly clock: DerbyClock;
  private readonly queue: RaceCommand[] = [];
  private readonly events: RaceEvent[] = [];
  private readonly laneParams: HorseParams[];
  private readonly laneNames: string[];
  private readonly ais: (AiController | null)[];
  private readonly aiStarts: (StartQuality | null)[];
  private readonly minTapTicks: number;
  private readonly perfectWindowTicks: number;
  private placeCounter = 0;
  private finishedCount = 0;
  private lastLeader = -1;
  private finalFurlongCalled = false;

  constructor(setup: RaceSetup) {
    if (!(setup.distanceM > 0)) throw new Error('RaceSim requires a positive distance.');
    this.setup = setup;
    const rules = setup.rules;
    this.clock = new DerbyClock(rules.tickS, MAX_STEPS_PER_FRAME);
    this.minTapTicks = Math.max(1, Math.round(rules.minTapIntervalS / rules.tickS));
    this.perfectWindowTicks = Math.max(1, Math.round(rules.perfectWindowS / rules.tickS));

    this.laneParams = [setup.player, ...setup.opponents.map((o) => o.params)];
    this.laneNames = [setup.playerName, ...setup.opponents.map((o) => o.name)];

    const master = new DerbyRngV1(setup.seed);
    this.ais = [null];
    this.aiStarts = [null];
    setup.opponents.forEach((opponent, index) => {
      const lane = index + 1;
      const fork = master.fork(`ai:${lane}`);
      const roll = fork.next();
      const quality: StartQuality =
        roll < setup.exec.perfectChance
          ? 'perfect'
          : roll < setup.exec.perfectChance + setup.exec.flinchChance
            ? 'flinch'
            : 'normal';
      this.aiStarts.push(quality);
      this.ais.push(createAi(opponent.params, opponent.policy, setup.exec, setup.distanceM, fork));
    });

    this.state = {
      tick: 0,
      phase: 'countdown',
      gunTick: Math.max(1, Math.round(rules.countdownS / rules.tickS)),
      lanes: this.laneParams.map((params) => ({
        x: 0,
        prevX: 0,
        v: 0,
        stamina: params.staminaMax,
        exhausted: false,
        lastTapTick: -10_000,
        start: 'pending',
        finishTick: null,
        finishTimeS: null,
      })),
      tape: [],
      tapsApplied: 0,
      exhaustedCount: 0,
      abandoned: false,
    };
  }

  get done(): boolean {
    return this.state.phase === 'done';
  }

  enqueue(command: RaceCommand): void {
    this.queue.push(command);
  }

  advance(nowMs: number): ClockFrame {
    return this.clock.advance(nowMs, this.speed, () => this.stepOnce());
  }

  drainEvents(): RaceEvent[] {
    return this.events.splice(0, this.events.length);
  }

  /** One fixed 30 Hz step. Exposed for tests, replays, and the harness. */
  stepOnce(): void {
    const state = this.state;
    if (state.phase === 'done') return;

    for (const command of this.queue.splice(0, this.queue.length)) {
      if (command.kind === 'abandon') {
        this.finishRace(true);
        return;
      }
      this.handlePlayerTap(state.tick);
    }

    if (state.phase === 'countdown' && state.tick >= state.gunTick) this.fireGun();

    if (state.phase === 'running') {
      const tS = (state.tick - state.gunTick) * this.setup.rules.tickS;
      for (let lane = 0; lane < state.lanes.length; lane += 1) this.stepLane(lane, tS);

      // The call of the race: lead changes and the run for home.
      let leader = 0;
      for (let lane = 1; lane < state.lanes.length; lane += 1) {
        if (state.lanes[lane]!.x > state.lanes[leader]!.x) leader = lane;
      }
      if (tS > 1.5 && leader !== this.lastLeader) {
        if (this.lastLeader >= 0) this.pushEvent({ kind: 'leadChange', lane: leader });
        this.lastLeader = leader;
      } else if (this.lastLeader < 0) {
        this.lastLeader = leader;
      }
      if (!this.finalFurlongCalled && state.lanes[leader]!.x >= this.setup.distanceM * 0.8) {
        this.finalFurlongCalled = true;
        this.pushEvent({ kind: 'finalFurlong' });
      }

      if (this.finishedCount === state.lanes.length || tS > this.setup.rules.timeoutS) {
        this.finishRace(false);
        return;
      }
    }

    state.tick += 1;
  }

  result(): RaceResult | null {
    const state = this.state;
    if (state.phase !== 'done') return null;
    const order = state.lanes
      .map((lane, index) => ({ lane: index, timeS: lane.finishTimeS }))
      .sort((a, b) => {
        if (a.timeS === null && b.timeS === null) return a.lane - b.lane;
        if (a.timeS === null) return 1;
        if (b.timeS === null) return -1;
        return a.timeS - b.timeS;
      });
    const placings = order.map((entry) => ({
      lane: entry.lane,
      name: this.laneNames[entry.lane] ?? `Lane ${entry.lane}`,
      timeS: entry.timeS,
    }));
    return {
      placings,
      playerPlace: order.findIndex((entry) => entry.lane === 0) + 1,
      playerTimeS: state.lanes[0]?.finishTimeS ?? null,
      taps: state.tapsApplied,
      exhaustedCount: state.exhaustedCount,
      tape: [...state.tape],
      seed: this.setup.seed,
      distanceM: this.setup.distanceM,
      abandoned: state.abandoned,
    };
  }

  laneName(lane: number): string {
    return this.laneNames[lane] ?? `Lane ${lane}`;
  }

  laneParamsOf(lane: number): HorseParams {
    const params = this.laneParams[lane];
    if (!params) throw new Error(`No lane ${lane}`);
    return params;
  }

  private pushEvent(event: RaceEvent): void {
    if (this.events.length >= EVENT_CAP) this.events.shift();
    this.events.push(event);
  }

  private fireGun(): void {
    this.state.phase = 'running';
    this.pushEvent({ kind: 'gun' });
    for (let lane = 1; lane < this.state.lanes.length; lane += 1) {
      const quality = this.aiStarts[lane] ?? 'normal';
      const laneState = this.state.lanes[lane]!;
      laneState.start = quality;
      this.pushEvent({ kind: 'start', lane, quality });
    }
  }

  private handlePlayerTap(tick: number): void {
    const state = this.state;
    state.tape.push(tick);
    const lane = state.lanes[0]!;
    const { gunTick } = state;

    if (tick < gunTick) {
      // Countdown taps: inside the early half of the perfect window they
      // read as a perfect break; earlier than that is a flinch — unless
      // the horse is a Cool Customer and simply doesn't spook.
      if (lane.start !== 'pending') return;
      if (tick >= gunTick - this.perfectWindowTicks) {
        lane.start = 'perfect';
        this.pushEvent({ kind: 'start', lane: 0, quality: 'perfect' });
      } else if (!this.setup.player.noFlinch) {
        lane.start = 'flinch';
        this.pushEvent({ kind: 'start', lane: 0, quality: 'flinch' });
      }
      return;
    }

    if (lane.start === 'pending') {
      const quality: StartQuality = tick <= gunTick + this.perfectWindowTicks ? 'perfect' : 'normal';
      lane.start = quality;
      this.pushEvent({ kind: 'start', lane: 0, quality });
      if (quality === 'perfect') {
        // The perfect break's tap is stamina-free.
        this.performTap(0, tick, true);
        return;
      }
    }
    this.performTap(0, tick, false);
  }

  /** Rank by distance among unfinished-or-not lanes; 1 = leading. */
  private rankOf(laneIndex: number): number {
    const x = this.state.lanes[laneIndex]!.x;
    let ahead = 0;
    for (let index = 0; index < this.state.lanes.length; index += 1) {
      if (index !== laneIndex && this.state.lanes[index]!.x > x) ahead += 1;
    }
    return ahead + 1;
  }

  /** Positional trait multipliers on tap impulse (Front Runner, Closer). */
  private traitImpulseMul(laneIndex: number): number {
    const params = this.laneParams[laneIndex]!;
    if (!params.frontRunner && !params.closer) return 1;
    const lane = this.state.lanes[laneIndex]!;
    const rank = this.rankOf(laneIndex);
    const fieldSize = this.state.lanes.length;
    let mul = 1;
    if (params.frontRunner) {
      if (rank === 1) mul *= 1.05;
      else if (rank === fieldSize) mul *= 0.97;
    }
    if (params.closer) {
      const progress = lane.x / this.setup.distanceM;
      if (progress >= 0.8 && rank >= Math.ceil(fieldSize / 2)) mul *= 1.08;
    }
    return mul;
  }

  private performTap(laneIndex: number, tick: number, free: boolean): void {
    const lane = this.state.lanes[laneIndex]!;
    const params = this.laneParams[laneIndex]!;
    if (lane.exhausted || lane.finishTick !== null) return;
    if (tick - lane.lastTapTick < this.minTapTicks) return;
    lane.v +=
      params.tapImpulse * this.traitImpulseMul(laneIndex) * Math.max(0, 1 - lane.v / params.vMax);
    lane.lastTapTick = tick;
    if (laneIndex === 0) this.state.tapsApplied += 1;
    if (!free) {
      lane.stamina -= params.tapCost;
      if (lane.stamina <= 0) {
        lane.stamina = 0;
        lane.exhausted = true;
        if (laneIndex === 0) this.state.exhaustedCount += 1;
        this.pushEvent({ kind: 'exhausted', lane: laneIndex });
      }
    }
    this.pushEvent({ kind: 'tap', lane: laneIndex });
  }

  private stepLane(laneIndex: number, tS: number): void {
    const lane = this.state.lanes[laneIndex]!;
    const params = this.laneParams[laneIndex]!;
    const rules = this.setup.rules;
    const dt = rules.tickS;

    if (lane.finishTick !== null) {
      // Past the wire: ease back to a canter for presentation.
      lane.prevX = lane.x;
      lane.v += -params.dragK * (lane.v - params.vBase) * dt;
      lane.x += lane.v * dt;
      return;
    }

    const ai = this.ais[laneIndex];
    if (ai && ai.maybeTap(tS, lane)) this.performTap(laneIndex, this.state.tick, false);

    // Gate phase: an honest head start that decays.
    if (tS < params.gateBoostS) {
      let mul = lane.start === 'perfect' ? rules.perfectGateMul : 1;
      if (lane.start === 'flinch' && tS < rules.flinchSuppressS) mul = 0;
      lane.v += params.gateAccel * mul * Math.max(0, 1 - lane.v / params.vMax) * dt;
    }

    const floor = lane.exhausted ? params.vExhausted : params.vBase;
    const k = lane.exhausted ? params.exhaustedDragK : params.dragK;
    lane.v += -k * (lane.v - floor) * dt;

    const sinceTapS = (this.state.tick - lane.lastTapTick) * dt;
    if (sinceTapS > params.regenDelayS && tS >= params.gateBoostS && lane.stamina < params.staminaMax) {
      const regen = params.regenPerS * (lane.exhausted ? params.exhaustedRegenMul : 1);
      lane.stamina = Math.min(params.staminaMax, lane.stamina + regen * dt);
      if (lane.exhausted && lane.stamina >= rules.recoverFraction * params.staminaMax) {
        lane.exhausted = false;
        this.pushEvent({ kind: 'recovered', lane: laneIndex });
      }
    }

    lane.prevX = lane.x;
    lane.x += lane.v * dt;

    if (lane.x >= this.setup.distanceM) {
      const traveled = lane.x - lane.prevX;
      const fraction = traveled > 0 ? (this.setup.distanceM - lane.prevX) / traveled : 0;
      lane.finishTick = this.state.tick;
      lane.finishTimeS = (this.state.tick - this.state.gunTick + fraction) * dt;
      this.placeCounter += 1;
      this.finishedCount += 1;
      this.pushEvent({ kind: 'finish', lane: laneIndex, place: this.placeCounter, timeS: lane.finishTimeS });
    }
  }

  private finishRace(abandoned: boolean): void {
    this.state.abandoned = abandoned;
    this.state.phase = 'done';
    this.pushEvent({ kind: 'raceDone' });
  }
}
